import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Shield, Zap, Users, ArrowRight, LogIn, Key, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SentinelLogin = () => {
  const navigate = useNavigate();
  const { login, user, setUser, checkAuth } = useAuth();
  const [showDevLogin, setShowDevLogin] = useState(false);
  const [devToken, setDevToken] = useState('');
  const [devLoading, setDevLoading] = useState(false);
  const [devError, setDevError] = useState('');

  const handleDevLogin = async () => {
    if (!devToken.trim()) return;
    setDevLoading(true);
    setDevError('');
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/dev-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token: devToken })
      });
      
      if (res.ok) {
        const data = await res.json();
        // Set user directly in context with session token
        setUser({
          user_id: data.user_id,
          email: data.email,
          name: data.name,
          is_admin: data.is_admin
        }, data.session_token);
        // Use React Router navigation instead of window.location
        navigate('/sentinel');
      } else {
        const err = await res.json();
        setDevError(err.detail || 'Invalid token');
      }
    } catch (error) {
      setDevError('Login failed');
    } finally {
      setDevLoading(false);
    }
  };

  if (user?.is_admin) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <Card className="max-w-md w-full bg-white/[0.03] border-white/10">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[#00F0FF]/10 flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-[#00F0FF]" />
            </div>
            <h2 className="font-mono text-xl text-[#F3F4F6] mb-2">Welcome Back</h2>
            <p className="font-sans text-[#6B7280] mb-6">
              Access the Social Sentinel dashboard
            </p>
            <Button
              onClick={() => navigate('/sentinel')}
              className="w-full bg-[#00F0FF] text-[#050505] hover:bg-[#00F0FF]/80"
            >
              Go to Dashboard
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <a href="/" className="font-mono text-xl tracking-widest text-[#F3F4F6] inline-block mb-4">
            WYCO<span className="text-[#00F0FF]">CAPITAL</span>
          </a>
          <h1 className="font-mono text-2xl text-[#F3F4F6] mb-2">Social Sentinel</h1>
          <p className="font-sans text-[#6B7280]">
            AI-powered social media intelligence for WyCo Capital
          </p>
        </div>

        {/* Features */}
        <div className="space-y-4 mb-8">
          <Card className="bg-white/[0.03] border-white/10">
            <CardContent className="p-4 flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-[#00F0FF]" />
              </div>
              <div>
                <h3 className="font-mono text-sm text-[#F3F4F6] mb-1">Automated Discovery</h3>
                <p className="font-sans text-xs text-[#6B7280]">
                  Monitor thought leaders and discover high-signal AI discussions automatically
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/[0.03] border-white/10">
            <CardContent className="p-4 flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-orange-400" />
              </div>
              <div>
                <h3 className="font-mono text-sm text-[#F3F4F6] mb-1">On-Brand Responses</h3>
                <p className="font-sans text-xs text-[#6B7280]">
                  AI-generated draft responses aligned with WyCo's investment thesis
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/[0.03] border-white/10">
            <CardContent className="p-4 flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center flex-shrink-0">
                <Shield className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h3 className="font-mono text-sm text-[#F3F4F6] mb-1">Human-in-the-Loop</h3>
                <p className="font-sans text-xs text-[#6B7280]">
                  Review and approve all responses before posting to X
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Login Button */}
        <Button
          onClick={login}
          className="w-full bg-[#00F0FF] text-[#050505] hover:bg-[#00F0FF]/80 h-12"
        >
          <LogIn className="w-5 h-5 mr-2" />
          Sign in with Google
        </Button>

        {/* Dev Login Toggle */}
        <div className="mt-6 pt-6 border-t border-white/10">
          <button
            onClick={() => setShowDevLogin(!showDevLogin)}
            className="w-full text-center font-mono text-xs text-[#6B7280] hover:text-[#F3F4F6] flex items-center justify-center gap-2"
          >
            <Key className="w-3 h-3" />
            {showDevLogin ? 'Hide' : 'Show'} Dev Login
          </button>
          
          {showDevLogin && (
            <div className="mt-4 space-y-3">
              <Input
                type="password"
                value={devToken}
                onChange={(e) => setDevToken(e.target.value)}
                placeholder="Dev token"
                className="bg-white/5 border-white/10 text-[#F3F4F6]"
                onKeyDown={(e) => e.key === 'Enter' && handleDevLogin()}
              />
              <Button
                onClick={handleDevLogin}
                disabled={devLoading || !devToken.trim()}
                variant="outline"
                className="w-full border-white/20 text-[#F3F4F6] hover:bg-white/10"
              >
                {devLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Login with Dev Token'}
              </Button>
              {devError && (
                <p className="text-xs text-red-400 text-center">{devError}</p>
              )}
            </div>
          )}
        </div>

        <p className="text-center font-sans text-xs text-[#6B7280] mt-4">
          Admin access required. Contact dustin@alphacollective.com for access.
        </p>
      </div>
    </div>
  );
};

export default SentinelLogin;
